// Copyright ©️ 2026 eVolve MEP, LLC

import type { CompanyMember, UserRoleId, AssignableRole, Entitlement } from '@evolvemep/api-ts';
import { ActionIcon, Flex, Text } from '@mantine/core';
import type { ColDef } from 'ag-grid-enterprise';

import { EvolveIcon } from 'assets/icons/EvolveIcon';
import { AgGridStyleTooltip } from 'components/Mantine/AgGridStyleTooltip';
import { CellRenderer } from 'components/Mantine/CellRenderer';
import { lockedColDef } from 'helpers/ag-grid/baseColumnDef';
import { customSetFilterParams } from 'helpers/ag-grid/convertAgGridRequest';
import { CustomSelectComponent } from 'helpers/ag-grid/CustomSelectComponent';
import { CustomValueSetFilterComponent } from 'helpers/ag-grid/CustomSetFilterComponent';
import { isNil, isNotNil } from 'helpers/isNotNil';
import type { RoleToRemove, HasProductsAvailable } from 'modules/Admin/CompanyMembers/types';

import { CompanyMemberMenu, type CompanyMembersBulkAction } from './CompanyMemberMenu';

const RoleComponent = ({
  roleId,
  roleName,
  setRemoveRole,
  user: { email, userId, isAdmin },
}: {
  roleId: UserRoleId | null;
  roleName: string | null;
  setRemoveRole?: (roleToRemove: RoleToRemove) => void;
  user: CompanyMember;
}) => (
  <Flex align="center" gap="xs" w="100%">
    <Text>{roleName}</Text>
    {!isAdmin && isNotNil(roleId) && setRemoveRole && (
      // Can only delete roles from non-admins
      <AgGridStyleTooltip
        label={
          <>
            Remove role <b>{roleName}</b> from this member
          </>
        }
        withArrow
      >
        <ActionIcon
          variant="subtle"
          className="show-on-row-hover"
          color="gray"
          onClick={() =>
            setRemoveRole({
              roleId,
              userId,
              userDisplayName: email,
            })
          }
        >
          <EvolveIcon icon="Delete" />
        </ActionIcon>
      </AgGridStyleTooltip>
    )}
  </Flex>
);

export const getCompanyMembersColDef = ({
  hasModifyCompanyMembersAccess,
  actionSingleUser,
  roles,
  setRemoveRole,
  hasProducts,
}: {
  hasModifyCompanyMembersAccess: boolean;
  actionSingleUser: (user: CompanyMember, selectedAction: CompanyMembersBulkAction) => void;
  roles: AssignableRole[];
  setRemoveRole: (roleToRemove: RoleToRemove) => void;
  hasProducts: HasProductsAvailable;
}): ColDef<CompanyMember>[] =>
  [
    {
      ...lockedColDef('left', true),
      width: 48,
      cellRenderer: 'agGroupCellRenderer',
    },
    {
      field: 'name',
      pinned: true,
      width: 200,
      filter: 'agTextColumnFilter',
      filterParams: {
        filterOptions: ['contains'],
        maxNumConditions: 1,
        buttons: ['reset'],
      },
    },
    {
      headerName: 'Email',
      field: 'email',
      flex: 1,
      minWidth: 250,
      filter: 'agTextColumnFilter',
      filterParams: {
        filterOptions: ['contains'],
        maxNumConditions: 1,
        buttons: ['reset'],
      },
    },
    {
      headerName: 'Company Role',
      field: 'companyRole',
      filter: CustomValueSetFilterComponent(
        customSetFilterParams(
          roles.filter((r) => r.scope === 'Company'),
          'roleId',
          'displayName',
          { filterKey: 'companyRoleId' },
        ),
      ),
      valueFormatter: ({ data }) => data?.companyRole ?? '',
      cellRenderer: CellRenderer<CompanyMember, 'companyRoleId'>(({ value, valueFormatted, data }) => (
        <RoleComponent roleId={value} roleName={valueFormatted ?? ''} user={data} setRemoveRole={setRemoveRole} />
      )),
    },
    {
      headerName: 'Electrical Role',
      field: 'electricalRoleId',
      hide: !hasProducts.electrical || !hasProducts.revitRoles,
      suppressColumnsToolPanel: !hasProducts.electrical || !hasProducts.revitRoles,
      sortable: false,
      filter: CustomValueSetFilterComponent(
        customSetFilterParams(
          roles.filter((r) => r.scope === 'EE'),
          'roleId',
          'displayName',
        ),
      ),
      cellClassRules: {
        'disabled-row': ({ data }) => !!data?.isAdmin,
      },
      valueFormatter: ({ data }) => (data?.isAdmin ? 'Admin' : (data?.electricalRole ?? '')),
      cellRenderer: CellRenderer<CompanyMember, 'electricalRoleId'>(({ value, valueFormatted, data }) => (
        <RoleComponent roleId={value} roleName={valueFormatted ?? ''} user={data} setRemoveRole={setRemoveRole} />
      )),
    },
    {
      headerName: 'Mechanical Role',
      field: 'mechanicalRoleId',
      hide: !hasProducts.mechanical || !hasProducts.revitRoles,
      suppressColumnsToolPanel: !hasProducts.mechanical || !hasProducts.revitRoles,
      sortable: false,
      filter: CustomValueSetFilterComponent(
        customSetFilterParams(
          roles.filter((r) => r.scope === 'EM'),
          'roleId',
          'displayName',
        ),
      ),
      cellClassRules: {
        'disabled-row': ({ data }) => !!data?.isAdmin,
      },
      valueFormatter: ({ data }) => (data?.isAdmin ? 'Admin' : (data?.mechanicalRole ?? '')),
      cellRenderer: CellRenderer<CompanyMember, 'mechanicalRoleId'>(({ value, valueFormatted, data }) => (
        <RoleComponent roleId={value} roleName={valueFormatted ?? ''} user={data} setRemoveRole={setRemoveRole} />
      )),
    },
    {
      headerName: 'Shop Role',
      field: 'shopRoleId',
      hide: !hasProducts.shop,
      suppressColumnsToolPanel: !hasProducts.shop,
      sortable: false,
      filter: CustomValueSetFilterComponent(
        customSetFilterParams(
          roles.filter((r) => r.scope === 'Shop'),
          'roleId',
          'displayName',
        ),
      ),
      cellClassRules: {
        'disabled-row': ({ data }) => !!data?.isAdmin,
      },
      valueFormatter: ({ data }) => (data?.isAdmin ? 'Admin' : (data?.shopRole ?? '')),
      cellRenderer: CellRenderer<CompanyMember, 'shopRoleId'>(({ value, valueFormatted, data }) => (
        <RoleComponent roleId={value} roleName={valueFormatted ?? ''} user={data} setRemoveRole={setRemoveRole} />
      )),
    },
    {
      headerName: 'Status',
      field: 'status',
      minWidth: 110,
      width: 150,
      cellStyle: ({ data }) => ({
        color: data?.status === 'Deleted' ? 'red' : data?.status === 'Active' ? 'teal' : 'orange',
      }),
      filter: CustomSelectComponent({
        label: 'Member status',
        defaultValue: 'Active, Pending',
        data: [
          {
            label: 'Active',
            value: 'Active, Pending',
          },
          {
            label: 'Deleted',
            value: 'Deleted',
          },
          {
            label: 'All',
            value: 'Active, Pending, Deleted',
          },
        ],
        filterKey: 'status',
      }),
    },
    {
      ...lockedColDef('right'),
      width: 56,
      cellRenderer: CellRenderer<CompanyMember>(
        ({ data }) =>
          isNotNil(actionSingleUser) ? (
            <CompanyMemberMenu actionSingleUser={actionSingleUser} user={data} hasProducts={hasProducts} />
          ) : null,
        { show: ({ data }) => data.status !== 'Deleted' },
      ),
      hide: !hasModifyCompanyMembersAccess || isNil(actionSingleUser),
    },
  ] as const;

export const getEntitlementsColDef = (
  hasModifyCompanyMembersAccess: boolean,
  setRemoveEntitlement: (entitlement: Entitlement) => void,
): ColDef<Entitlement>[] =>
  [
    {
      ...lockedColDef(),
      width: 60,
      hide: !hasModifyCompanyMembersAccess,
      cellRenderer: CellRenderer<Entitlement>(({ data }) => (
        <AgGridStyleTooltip
          label={
            <>
              Remove license <b>{data.productPool.product.productNameFormatted}</b> from this member
            </>
          }
          withArrow
        >
          <ActionIcon color="gray" onClick={() => setRemoveEntitlement(data)}>
            <EvolveIcon className="show-on-row-hover" icon="Delete" />
          </ActionIcon>
        </AgGridStyleTooltip>
      )),
    },
    {
      headerName: 'License Name',
      field: 'productPool.product.productNameFormatted',
      flex: 1,
    },
  ] as const;
